<template>
  <CustomDropdown
    ref="customDropdownRef"
    dropdownCSS="p-0"
    maxHeight="400px"
    :position="dropdownPosition"
    gap="10px"
    :relative-to-viewport="useRelativeToViewport"
  >
    <template #trigger>
      <div class="flex flex-row gap-1 items-center cursor-pointer text-sm">
        <ThemeImage
          :icon="imageNames.ADD_GREY"
          alt="insert_icon"
          class="size-5"
        />
        Insert
      </div>
    </template>

    <template #default>
      <!-- Main Menu -->
      <div v-if="!currentDropdown" class="flex flex-col gap-1 p-2">
        <span class="text-[11px] text_color opacity-60 mx-3 mt-2">INSERT</span>

        <!-- Signature Option -->
        <div
          v-if="showSignature"
          class="dropdown_item flex flex-row w-full justify-between"
          @click="addSignature"
        >
          <div class="flex flex-row items-center gap-2">
            <ThemeImage
              :icon="imageNames.SIGNATURE_GREY"
              alt="signature_icon"
              class="size-4"
            />
            <span class="text-sm">Signature</span>
          </div>
        </div>

        <!-- Sendspark Video Option -->
        <div
          v-if="showSendsparkVideo"
          class="dropdown_item flex flex-row w-full justify-between"
          @click="showDropdown('sendspark')"
        >
          <div class="flex flex-row items-center gap-2">
            <ThemeImage
              :icon="imageNames.SENDSPARK"
              alt="sendspark_icon"
              class="size-4"
            />
            <span class="text-sm">Sendspark Video</span>
          </div>
          <ThemeImage
            :icon="imageNames.RIGHT_ARROW_SMALL_GREY"
            alt="right_arrow_small"
            class="size-4"
          />
        </div>

        <!-- Meeting Agent Option -->
        <div
          v-if="showMeetingAgent"
          class="dropdown_item flex flex-row w-full justify-between"
          @click="showDropdown('meetingAgent')"
        >
          <div class="flex flex-row items-center gap-2">
            <ThemeImage
              :icon="imageNames.MEETING_GREY"
              alt="meeting_icon"
              class="size-4"
            />
            <span class="text-sm">Meeting Agent</span>
          </div>
          <ThemeImage
            :icon="imageNames.RIGHT_ARROW_SMALL_GREY"
            alt="right_arrow_small"
            class="size-4"
          />
        </div>
      </div>

      <!-- Meeting Agent Dropdown -->
      <div
        v-if="currentDropdown === 'meetingAgent'"
        class="flex flex-col gap-1 w-[300px]"
      >
        <div class="flex items-center gap-2 px-3 pb-3 pt-4">
          <ThemeImage
            @click="closeCurrentDropdown"
            :icon="imageNames.BACK_ARROW_WHITE"
            alt="back_arrow_icon"
            class="cursor-pointer"
          />
          <div class="text-sm font-medium text_color">Insert Meeting Agent</div>
        </div>

        <div
          class="flex flex-col secondary_color text-[13px] p-3 gap-5 border_top border_bottom"
        >
          <div
            v-if="!props.stepType.includes('LINKEDIN')"
            class="flex flex-col gap-1"
          >
            <span>Link Display Text</span>
            <a-input
              v-model:value="meetingAgentForm.linkDisplayText"
              placeholder="Enter link display text"
              class="input_medium"
            />
          </div>

          <div class="flex flex-col gap-1">
            <span>Meeting Agent</span>
            <a-select
              v-model:value="meetingAgentForm.selectedAgent"
              placeholder="Select a meeting agent"
              :allowClear="true"
              dropdownClassName="meeting-agent-dropdown"
              :dropdownMatchSelectWidth="false"
              :getPopupContainer="getPopupContainer"
            >
              <template v-if="agentDetails.agents?.length > 0">
                <a-select-option
                  v-for="agent in agentDetails.agents"
                  :key="agent.id"
                  :value="agent.public_link"
                  class="flex flex-col gap-1"
                >
                  <div>
                    <div class="font-medium text_color">{{ agent.name }}</div>
                    <div class="secondary_color font-light text-xs mt-1">
                      {{
                        agent.type === "round_robin"
                          ? "Round Robin"
                          : "One-on-one"
                      }}
                    </div>
                    <div
                      style="background: var(--white-opacity-10)"
                      class="mt-1 text_color text-xs px-2 py-0.5 rounded-lg inline-block"
                    >
                      {{ agent.duration }}min
                    </div>
                  </div>
                </a-select-option>
              </template>
              <template v-else>
                <a-select-option disabled>
                  <div class="text-center p-4">No meeting agents available</div>
                </a-select-option>
              </template>

              <template #dropdownRender="{ menuNode: menu }">
                <div>
                  <div class="overflow-y-auto max-h-[200px]">
                    <v-nodes :vnodes="menu" />
                  </div>
                  <div
                    class="select_vnode mt-1 border-t border-[#2d2d30] pt-2"
                    @click="goToManageMeetingAgentPage"
                  >
                    <ThemeImage
                      class="size-[14px]"
                      :icon="imageNames.SETTINGS_GREY"
                      alt="settings_icon"
                    />
                    <span class="text-sm">Manage Meeting Agents</span>
                  </div>
                </div>
              </template>
            </a-select>
          </div>
        </div>

        <div class="flex justify-between items-center p-2 pb-3">
          <a-button @click="closeAllDropdowns" class="secondary_small"
            >Cancel</a-button
          >
          <a-button
            @click="insertMeetingAgent"
            class="primary_small"
            :disabled="
              props.stepType.includes('LINKEDIN')
                ? !meetingAgentForm.selectedAgent
                : !meetingAgentForm.linkDisplayText ||
                  !meetingAgentForm.selectedAgent
            "
          >
            Insert
          </a-button>
        </div>
      </div>

      <!-- Sendspark Video Dropdown -->
      <div
        v-if="currentDropdown === 'sendspark'"
        class="flex flex-col w-[300px]"
      >
        <div class="flex items-center gap-2 px-3 pb-3 pt-4">
          <ThemeImage
            @click="closeCurrentDropdown"
            :icon="imageNames.BACK_ARROW_WHITE"
            alt="back_arrow_icon"
            class="cursor-pointer"
          />
          <div class="text-sm text_color font-medium">Insert Video</div>
        </div>

        <div class="px-3 pt-3 border_top">
          <div class="relative">
            <ThemeImage
              :icon="imageNames.SEARCH_VARIANT1"
              alt="search"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 opacity-50"
            />
            <a-input
              v-model="sendsparkForm.searchQuery"
              placeholder="Search here"
              class="input_medium"
            />
          </div>
        </div>

        <div class="px-3 flex-1 overflow-y-auto max-h-[180px] mt-3">
          <div
            v-if="!filteredCampaigns.length"
            class="text-center p-4 secondary_color"
          >
            No videos available
          </div>

          <template v-else>
            <div
              v-for="campaign in filteredCampaigns"
              :key="campaign.id"
              @click="
                !sendsparkForm.insertedIds.has(campaign.id) &&
                toggleCampaignSelection(campaign)
              "
              class="dropdown_item mb-1"
              :class="[
                {
                  bg_white_8:
                    sendsparkForm.selectedIds.includes(campaign.id) ||
                    sendsparkForm.insertedIds.has(campaign.id),
                  'cursor-not-allowed': sendsparkForm.insertedIds.has(
                    campaign.id,
                  ),
                },
              ]"
            >
              <div class="flex flex-col gap-1 flex-1">
                <div class="text-sm text_color truncate max-w-[220px]">
                  {{ campaign.campaign_name }}
                </div>
              </div>
              <div
                v-if="
                  sendsparkForm.selectedIds.includes(campaign.id) ||
                  sendsparkForm.insertedIds.has(campaign.id)
                "
              >
                <ThemeImage
                  :icon="imageNames.CHECK"
                  alt="selected"
                  class="w-5 h-5"
                />
              </div>
            </div>
          </template>
        </div>

        <div
          class="px-3 pt-3 pb-3 border_top flex justify-between items-center"
        >
          <a-button @click="closeAllDropdowns" class="secondary_small"
            >Cancel</a-button
          >
          <a-button
            @click="insertSendsparkVideos"
            class="primary_small"
            :disabled="sendsparkForm.selectedIds.length === 0"
          >
            Insert
          </a-button>
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import {
  ref,
  computed,
  defineComponent,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";

import DashboardService from "@/src/api/DashboardService.js";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import { insertOptions } from "@/src/constants/settingConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const emit = defineEmits(["update:modelValue", "insert-sendspark-video"]);

const props = defineProps({
  stepType: {
    type: String,
    default: "EMAIL",
  },
  useRelativeToViewport: {
    type: Boolean,
    default: false,
  },
});

const VNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true,
    },
  },
  render() {
    return this.vnodes;
  },
});

const router = useRouter();
const customDropdownRef = ref(null);
const currentDropdown = ref(null);
const sendsparkCampaigns = ref([]);
const sendsparkIntegration = ref(false);

const dropdownPosition = computed(() => {
  return props.stepType === "CONVERSATION_EMAIL" || props.stepType === "EMAIL"
    ? "topstart"
    : "topend";
});

const showSignature = computed(() => {
  return !props.stepType.includes("LINKEDIN");
});

const showSendsparkVideo = computed(() => {
  return (
    props.stepType !== insertOptions.LINKEDIN_INVITE &&
    sendsparkIntegration.value
  );
});

const showMeetingAgent = computed(() => {
  return (
    (props.stepType.includes("LINKEDIN") || props.stepType.includes("EMAIL")) &&
    props.stepType !== insertOptions.AI_SDR
  );
});

const meetingAgentForm = ref({
  linkDisplayText: "",
  selectedAgent: null,
});

const sendsparkForm = ref({
  searchQuery: "",
  selectedIds: [],
  insertedIds: new Set(),
});

const agentDetails = ref({
  success: false,
  calendar_integrated: false,
  agents: [],
});

const showDropdown = (type) => {
  currentDropdown.value = type;
};

const closeCurrentDropdown = () => {
  currentDropdown.value = null;
};

const closeAllDropdowns = () => {
  currentDropdown.value = null;
  meetingAgentForm.value = {
    linkDisplayText: "",
    selectedAgent: null,
  };
  sendsparkForm.value = {
    ...sendsparkForm.value,
    searchQuery: "",
    selectedIds: [],
  };
  customDropdownRef.value?.closeDropdown();
};

const insertMeetingAgent = () => {
  if (props.stepType.includes("LINKEDIN")) {
    emit("update:modelValue", meetingAgentForm.value.selectedAgent);
  } else {
    emit("update:modelValue", {
      url: meetingAgentForm.value.selectedAgent,
      text: meetingAgentForm.value.linkDisplayText,
    });
  }
  closeAllDropdowns();
};

const toggleCampaignSelection = (campaign) => {
  const index = sendsparkForm.value.selectedIds.indexOf(campaign.id);
  if (index === -1) {
    sendsparkForm.value.selectedIds.push(campaign.id);
  } else {
    sendsparkForm.value.selectedIds.splice(index, 1);
  }
};

const goToManageMeetingAgentPage = () => {
  router.push({ path: "/meeting_agent" });
};

const fetchAgents = async () => {
  try {
    const params = {
      page_size: -1,
      page: 1,
      search_query: "",
    };
    const response = await DashboardService.fetchMeetingAgents(params);
    if (response.success && response.calendar_integrated) {
      agentDetails.value = response;
      console.log("Fetched agents:", agentDetails.value.agents);
    }
  } catch (error) {
    console.error("Error fetching agents:", error);
    agentDetails.value = {
      success: false,
      calendar_integrated: false,
      agents: [],
    };
  }
};

const fetchSendsparkCampaigns = async () => {
  try {
    const response = await DashboardService.getSendsparkDynamicVideos();
    if (response.success) {
      sendsparkCampaigns.value = response.campaigns;
      sendsparkIntegration.value = response.integration;
    } else {
      message.error("Failed to fetch sendspark campaigns");
    }
  } catch (error) {
    console.error("Error fetching sendspark campaigns:", error);
    message.error("An error occurred while fetching sendspark campaigns");
  }
};

const filteredCampaigns = computed(() => {
  if (!sendsparkForm.value.searchQuery?.trim()) {
    return sendsparkCampaigns.value;
  }
  const searchTerm = sendsparkForm.value.searchQuery.toLowerCase().trim();
  return sendsparkCampaigns.value.filter((campaign) =>
    campaign?.campaign_name?.toLowerCase().includes(searchTerm),
  );
});

const insertSendsparkVideos = () => {
  const selectedCampaigns = sendsparkCampaigns.value.filter((campaign) =>
    sendsparkForm.value.selectedIds.includes(campaign.id),
  );

  selectedCampaigns.forEach((campaign) => {
    sendsparkForm.value.insertedIds.add(campaign.id);
    emit("insert-sendspark-video", {
      title: campaign.campaign_name,
      link: `${campaign.campaign_link}{{contact.add_sendspark_email}}`,
      thumbnail: `${campaign.thumbnail_link}?email=contact.add_sendspark_email`,
    });
  });
  closeAllDropdowns();
};

const addSignature = () => {
  emit("update:modelValue", "{{ email.signature | default: ''}}");
  closeAllDropdowns();
};

const getPopupContainer = (triggerNode) => {
  return triggerNode?.parentNode || document.body;
};

const handleClickOutside = (event) => {
  if (
    customDropdownRef.value &&
    !customDropdownRef.value.$el.contains(event.target)
  ) {
    currentDropdown.value = null;
    closeAllDropdowns();
  }
};

onMounted(() => {
  fetchAgents();
  fetchSendsparkCampaigns();
  document.addEventListener("mousedown", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
});

fetchAgents();
</script>

<style>
.ant-modal-content {
  background-color: #121212 !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.ant-modal-header {
  background-color: #121212 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.ant-modal-title {
  color: white !important;
}

.ant-modal-body {
  background-color: #121212 !important;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 2px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #1d1d20;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #1d1d20;
  border-radius: 1px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #1d1d20;
}
:deep(.ant-select-dropdown) {
  background-color: #1d1d20 !important;
  border: 1px solid #2d2d30 !important;
}

:deep(.ant-select-item) {
  color: white !important;
  background-color: transparent !important;
}

:deep(.ant-select-item-option-selected) {
  background-color: #2d2d30 !important;
}

:deep(.ant-select-item-option-active) {
  background-color: #3a3a3a !important;
}

:deep(.meeting-agent-dropdown) {
  max-height: 250px !important;
  z-index: 9999 !important;
  position: fixed !important;
}

:deep(.meeting-agent-dropdown .ant-select-dropdown-menu) {
  max-height: 200px !important;
  overflow-y: auto !important;
}
</style>
